import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Ticket } from "lucide-react";
import { scrollToId } from "@/data";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "GEN", "FEB", "MAR", "APR", "MAG", "GIU",
  "LUG", "AGO", "SET", "OTT", "NOV", "DIC",
];
const DAYS = ["DOM", "LUN", "MAR", "MER", "GIO", "VEN", "SAB"];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 },
  }),
};

function ConcertRow({ concert, index, past, next, testid }) {
  const d = new Date(`${concert.date}T12:00:00`);
  return (
    <motion.li
      variants={reveal}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      data-testid={testid}
      className={`group grid grid-cols-[auto_1fr] items-center gap-5 border-b border-neutral-800/70 py-6 transition-colors duration-300 sm:grid-cols-[110px_1fr_auto] sm:gap-8 ${
        past ? "opacity-60 hover:opacity-100" : "hover:bg-red-950/10"
      }`}
    >
      <div className="flex flex-col items-start leading-none">
        <span className="font-mono-tac text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          {DAYS[d.getDay()]}
        </span>
        <span
          className={`font-display mt-1 text-5xl transition-colors duration-300 sm:text-6xl ${
            past
              ? "text-neutral-500"
              : "text-neutral-100 group-hover:text-[#C8102E]"
          }`}
        >
          {String(d.getDate()).padStart(2, "0")}
        </span>
        <span
          className={`font-mono-tac text-xs uppercase tracking-[0.3em] ${
            past ? "text-neutral-600" : "text-red-600"
          }`}
        >
          {MONTHS[d.getMonth()]} {d.getFullYear()}
        </span>
      </div>

      <div className="min-w-0">
        <h3 className="font-serif-logo text-xl uppercase tracking-[0.15em] text-neutral-100 sm:text-2xl">
          {concert.city}
        </h3>
        <p className="mt-1 flex items-center gap-2 text-sm text-neutral-400">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-red-700" />
          <span>{concert.title}</span>
          {concert.time && (
            <>
              <span className="text-neutral-700">·</span>
              <span className="font-mono-tac text-xs">{concert.time}</span>
            </>
          )}
        </p>
        {concert.note && (
          <p className="mt-1 text-xs italic text-neutral-500">{concert.note}</p>
        )}
      </div>

      <div className="col-span-2 flex items-center gap-3 sm:col-span-1">
        {next && (
          <span className="inline-flex items-center gap-2 border border-red-900/60 px-3 py-1.5 font-mono-tac text-[10px] uppercase tracking-[0.25em] text-red-500">
            <span className="live-dot inline-flex h-1.5 w-1.5 rounded-full bg-red-600" />
            Prossima
          </span>
        )}
        {!past && concert.ticketUrl && (
          <a
            data-testid={`${testid}-ticket`}
            href={concert.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#C8102E] px-4 py-2 font-mono-tac text-xs uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-[#FF2A4B] hover:shadow-[0_0_20px_rgba(200,16,46,0.5)]"
          >
            <Ticket className="h-3.5 w-3.5" />
            Biglietti
          </a>
        )}
        {past && (
          <span className="font-mono-tac text-[10px] uppercase tracking-[0.25em] text-neutral-600">
            Già suonata
          </span>
        )}
      </div>
    </motion.li>
  );
}

export default function Concerts() {
  const [concerts, setConcerts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/concerts`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setConcerts)
      .catch(() => setConcerts([]))
      .finally(() => setLoaded(true));
  }, []);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = concerts.filter((c) => c.date >= today).sort((a, b) =>
    a.date.localeCompare(b.date)
  );
  const past = concerts.filter((c) => c.date < today).sort((a, b) =>
    b.date.localeCompare(a.date)
  );

  return (
    <section
      id="live"
      data-testid="concerts-section"
      className="relative overflow-hidden border-t border-neutral-900 bg-[#090909]"
    >
      <div className="pointer-events-none absolute -right-32 top-20 h-[28rem] w-[28rem] bg-[radial-gradient(circle,rgba(200,16,46,0.16),transparent_65%)] blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
        <motion.p
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="font-mono-tac text-xs uppercase tracking-[0.35em] text-red-600"
        >
          02 — Dal vivo
        </motion.p>
        <motion.h2
          variants={reveal}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="font-display mt-4 text-5xl uppercase leading-none text-neutral-100 sm:text-7xl"
        >
          Prossime <span className="text-outline-red">date</span>
        </motion.h2>
        <motion.p
          variants={reveal}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400"
        >
          Il disco è una cosa. Il muro di suono dal vivo è un'altra. Vieni a
          sentirci.
        </motion.p>

        <div className="mt-14 max-w-4xl">
          {!loaded ? (
            <p data-testid="concerts-loading" className="font-mono-tac text-xs uppercase tracking-[0.3em] text-neutral-600">
              Caricamento date…
            </p>
          ) : upcoming.length > 0 ? (
            <ul data-testid="concerts-upcoming-list" className="border-t border-neutral-800/70">
              {upcoming.map((c, i) => (
                <ConcertRow
                  key={c.id}
                  concert={c}
                  index={i}
                  next={i === 0}
                  testid={`concert-upcoming-${i + 1}`}
                />
              ))}
            </ul>
          ) : (
            <p
              data-testid="concerts-empty"
              className="border border-dashed border-neutral-800 p-10 font-mono-tac text-xs uppercase tracking-[0.3em] text-neutral-500"
            >
              Nessuna data in programma. Nuove date in arrivo.
            </p>
          )}

          <motion.button
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            data-testid="concerts-booking-cta"
            onClick={() => scrollToId("booking")}
            className="mt-8 inline-flex items-center gap-3 border border-neutral-700 px-6 py-3 font-mono-tac text-xs uppercase tracking-[0.25em] text-neutral-300 transition-all duration-200 hover:border-red-600 hover:text-red-500"
          >
            Vuoi portarci nella tua città? →
          </motion.button>

          {past.length > 0 && (
            <div className="mt-20">
              <motion.p
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="font-mono-tac mb-6 text-[10px] uppercase tracking-[0.35em] text-neutral-500"
              >
                Dove abbiamo già suonato
              </motion.p>
              <ul data-testid="concerts-past-list" className="border-t border-neutral-800/70">
                {past.map((c, i) => (
                  <ConcertRow
                    key={c.id}
                    concert={c}
                    index={i}
                    past
                    testid={`concert-past-${i + 1}`}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
